/**
 * chat_langs.ts — the languages live chat translates between (2026-09-24). MIRRORED byte-identical at
 * client/src/lib/chat_langs.ts (verify:world-wave diffs them).
 *
 * A player's chat language is declared, not guessed: the client sets it from the browser's language on entry
 * (`setChatLanguage`), and a line is taken to be in its SENDER's declared language unless its script says
 * otherwise (a line in kana is Japanese whoever typed it). Short chat lines defeat statistical detectors; a
 * declaration plus a script check does not.
 */

export interface ChatLang {
  code: string;
  name: string;
  /** NLLB-200 code (the optional engine). */
  nllb: string;
}

export const CHAT_LANGS: ChatLang[] = [
  { code: 'en', name: 'English', nllb: 'eng_Latn' },
  { code: 'es', name: 'Español', nllb: 'spa_Latn' },
  { code: 'fr', name: 'Français', nllb: 'fra_Latn' },
  { code: 'de', name: 'Deutsch', nllb: 'deu_Latn' },
  { code: 'it', name: 'Italiano', nllb: 'ita_Latn' },
  { code: 'pt', name: 'Português', nllb: 'por_Latn' },
  { code: 'nl', name: 'Nederlands', nllb: 'nld_Latn' },
  { code: 'pl', name: 'Polski', nllb: 'pol_Latn' },
  { code: 'sv', name: 'Svenska', nllb: 'swe_Latn' },
  { code: 'da', name: 'Dansk', nllb: 'dan_Latn' },
  { code: 'no', name: 'Norsk', nllb: 'nob_Latn' },
  { code: 'fi', name: 'Suomi', nllb: 'fin_Latn' },
  { code: 'cs', name: 'Čeština', nllb: 'ces_Latn' },
  { code: 'hu', name: 'Magyar', nllb: 'hun_Latn' },
  { code: 'ro', name: 'Română', nllb: 'ron_Latn' },
  { code: 'tr', name: 'Türkçe', nllb: 'tur_Latn' },
  { code: 'el', name: 'Ελληνικά', nllb: 'ell_Grek' },
  { code: 'ru', name: 'Русский', nllb: 'rus_Cyrl' },
  { code: 'uk', name: 'Українська', nllb: 'ukr_Cyrl' },
  { code: 'ar', name: 'العربية', nllb: 'arb_Arab' },
  { code: 'he', name: 'עברית', nllb: 'heb_Hebr' },
  { code: 'hi', name: 'हिन्दी', nllb: 'hin_Deva' },
  { code: 'bn', name: 'বাংলা', nllb: 'ben_Beng' },
  { code: 'th', name: 'ไทย', nllb: 'tha_Thai' },
  { code: 'vi', name: 'Tiếng Việt', nllb: 'vie_Latn' },
  { code: 'id', name: 'Bahasa Indonesia', nllb: 'ind_Latn' },
  { code: 'ja', name: '日本語', nllb: 'jpn_Jpan' },
  { code: 'ko', name: '한국어', nllb: 'kor_Hang' },
  { code: 'zh', name: '中文', nllb: 'zho_Hans' },
];

const BY_CODE = new Map(CHAT_LANGS.map((l) => [l.code, l]));

export function chatLang(code: string): ChatLang | null {
  return BY_CODE.get(code) ?? null;
}

/** A browser language tag ('pt-BR', 'zh-Hant-TW', 'nb') → the supported base code, or 'en'. */
export function normalizeLang(tag: string): string {
  const base = String(tag || '').toLowerCase().split(/[-_]/)[0];
  if (base === 'nb' || base === 'nn') return 'no';
  if (base === 'iw') return 'he';
  return BY_CODE.has(base) ? base : 'en';
}

/**
 * The language a line's SCRIPT pins down, or '' when its script is shared (Latin, or nothing but digits and
 * punctuation). Kana beats Han (Japanese writes both); Han alone is Chinese.
 */
export function scriptLang(text: string): string {
  let han = 0, kana = 0, hangul = 0, cyr = 0, arab = 0, hebr = 0, deva = 0, beng = 0, thai = 0, greek = 0, ukr = 0;
  for (const ch of text) {
    const c = ch.codePointAt(0) ?? 0;
    if (c >= 0x3040 && c <= 0x30ff) kana++;
    else if ((c >= 0x4e00 && c <= 0x9fff) || (c >= 0x3400 && c <= 0x4dbf)) han++;
    else if ((c >= 0xac00 && c <= 0xd7af) || (c >= 0x1100 && c <= 0x11ff)) hangul++;
    else if (c >= 0x0400 && c <= 0x04ff) { cyr++; if (c === 0x0456 || c === 0x0457 || c === 0x0454 || c === 0x0491 || c === 0x0406 || c === 0x0407 || c === 0x0404 || c === 0x0490) ukr++; }
    else if (c >= 0x0600 && c <= 0x06ff) arab++;
    else if (c >= 0x0590 && c <= 0x05ff) hebr++;
    else if (c >= 0x0900 && c <= 0x097f) deva++;
    else if (c >= 0x0980 && c <= 0x09ff) beng++;
    else if (c >= 0x0e00 && c <= 0x0e7f) thai++;
    else if (c >= 0x0370 && c <= 0x03ff) greek++;
  }
  if (kana > 0) return 'ja';
  if (hangul > 0) return 'ko';
  if (han > 0) return 'zh';
  if (cyr > 0) return ukr > 0 ? 'uk' : 'ru';
  if (arab > 0) return 'ar';
  if (hebr > 0) return 'he';
  if (deva > 0) return 'hi';
  if (beng > 0) return 'bn';
  if (thai > 0) return 'th';
  if (greek > 0) return 'el';
  return '';
}

/** The language a line is taken to be in: its script's, else its sender's declared language. */
export function lineLang(text: string, senderLang: string): string {
  return scriptLang(text) || (BY_CODE.has(senderLang) ? senderLang : 'en');
}

/** Worth translating at all? Nothing but emoji, digits and punctuation — or a single short token — is not. */
export function translatable(text: string): boolean {
  const letters = text.replace(/[\s\d\p{P}\p{S}]/gu, '');
  return letters.length >= 2;
}
